import type { PuckComponent } from "./types.js";
import { isRecord } from "../utils.js";
import { findHoverPreset } from "../dss/hover-presets.js";
import {
  buildBackgroundEffectCss,
  buildGradient,
  rgbaColor,
} from "../dss/compute-styles.js";
import type { DssBackground, DssStyles } from "../dss/types.js";

type StyleState = "normal" | "hover" | "focus" | "active";

const ALLOWED_STYLE_PROPERTIES = new Set([
  "background",
  "backgroundColor",
  "backgroundImage",
  "backgroundPosition",
  "backgroundSize",
  "backgroundRepeat",
  "backgroundAttachment",
  "position",
  "isolation",
  "opacity",
  "color",
  "textAlign",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textTransform",
  "textDecoration",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "padding",
  "margin",
  "border",
  "borderStyle",
  "borderColor",
  "borderWidth",
  "borderRadius",
  "boxShadow",
  "outline",
  "transform",
]);

const VALID_SCOPED_CLASS = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const SAFE_COMPONENT_ID = /^[a-zA-Z0-9_-]{1,64}$/;

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function toNumber(value: unknown, fallback: number): number {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function camelToKebab(value: string): string {
  return value.replace(/([A-Z])/g, "-$1").toLowerCase();
}

function kebabToCamel(value: string): string {
  return value.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

function sanitizeCssValue(value: string): string {
  let safe = value;
  safe = safe.replace(/[{}]/g, "");
  safe = safe.replace(/\/\*/g, "");
  safe = safe.replace(/\*\//g, "");
  safe = safe.replace(/<\/style>/gi, "");
  safe = safe.replace(/@import/gi, "");
  safe = safe.replace(/@charset/gi, "");
  safe = safe.replace(/expression\s*\(/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*javascript\s*:/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*data:\s*text\/html/gi, "");
  safe = safe.replace(/\\/g, "");
  return safe.trim();
}

function sanitizeComponentId(id: string): string {
  const trimmed = id.trim();
  if (SAFE_COMPONENT_ID.test(trimmed)) return trimmed;

  return trimmed
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function buildScopedClassName(componentId: string): string {
  const safeId = sanitizeComponentId(componentId);
  return safeId ? `puck-block-${safeId}` : "puck-block-unknown";
}

function sanitizeStyleMap(styles: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => ALLOWED_STYLE_PROPERTIES.has(key))
      .map(([key, value]) => [key, sanitizeCssValue(value)] as const)
      .filter(([, value]) => value.length > 0),
  );
}

function parseDeclarations(css: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const declaration of css.split(";")) {
    const trimmed = declaration.trim();
    if (!trimmed) continue;

    const colonIndex = trimmed.indexOf(":");
    if (colonIndex <= 0) continue;

    const property = kebabToCamel(trimmed.slice(0, colonIndex).trim());
    const value = trimmed.slice(colonIndex + 1).trim();
    if (!ALLOWED_STYLE_PROPERTIES.has(property)) continue;
    if (!value) continue;
    result[property] = value;
  }

  return result;
}

function buildBackgroundStyles(background: Record<string, unknown>): Record<string, string> {
  const mode = asString(background.mode) ?? "none";
  const effect = isRecord(background.effect) ? background.effect : null;
  const hasActiveEffect = Boolean(effect && asString(effect.preset) && asString(effect.preset) !== "none");
  const effectShell: Record<string, string> = hasActiveEffect
    ? { position: "relative", isolation: "isolate" }
    : {};

  if (mode === "color") {
    const color = asString(background.color);
    if (!color) return {};
    const opacity = clamp(toNumber(background.opacity, 100), 0, 100);
    return sanitizeStyleMap({
      backgroundColor: rgbaColor(color, opacity),
      ...effectShell,
    } as Record<string, string>);
  }

  if (mode === "gradient" && isRecord(background.gradient)) {
    const gradient = buildGradient(background as unknown as DssBackground);
    return gradient ? sanitizeStyleMap({
      backgroundImage: gradient,
      ...effectShell,
    } as Record<string, string>) : {};
  }

  if (mode === "image" && isRecord(background.image)) {
    const url = asString(background.image.url);
    if (!url) return {};
    const positionX = clamp(toNumber(background.image.positionX, 50), 0, 100);
    const positionY = clamp(toNumber(background.image.positionY, 50), 0, 100);
    const size = asString(background.image.size) ?? "cover";
    const customWidth = asString(background.image.customWidth) ?? "auto";
    const customHeight = asString(background.image.customHeight) ?? "auto";
    const resolvedSize = size === "custom" ? `${customWidth} ${customHeight}` : size;

    return sanitizeStyleMap({
      backgroundImage: `url("${url}")`,
      backgroundPosition: `${positionX}% ${positionY}%`,
      backgroundSize: resolvedSize,
      backgroundRepeat: asString(background.image.repeat) ?? "no-repeat",
      backgroundAttachment: asString(background.image.attachment) ?? "scroll",
      ...effectShell,
    } as Record<string, string>);
  }

  if (hasActiveEffect) {
    return sanitizeStyleMap({
      position: "relative",
      isolation: "isolate",
    });
  }

  return {};
}

function buildTypographyStyles(typography: Record<string, unknown>): Record<string, string> {
  const styles: Record<string, string> = {};

  const fontFamily = asString(typography.fontFamily);
  if (fontFamily) {
    styles.fontFamily = `'${fontFamily}', sans-serif`;
  }

  const fontSize = toNumber(typography.fontSize, 16);
  const fontSizeUnit = asString(typography.fontSizeUnit) ?? "px";
  styles.fontSize = `${fontSize}${fontSizeUnit}`;
  styles.fontWeight = String(toNumber(typography.fontWeight, 400));

  const fontStyle = asString(typography.fontStyle);
  if (fontStyle && fontStyle !== "normal") styles.fontStyle = fontStyle;

  const textTransform = asString(typography.textTransform);
  if (textTransform && textTransform !== "none") styles.textTransform = textTransform;

  const textDecoration = asString(typography.textDecoration);
  if (textDecoration && textDecoration !== "none") styles.textDecoration = textDecoration;

  const lineHeight = toNumber(typography.lineHeight, 1.5);
  const lineHeightUnit = asString(typography.lineHeightUnit) ?? "";
  styles.lineHeight = `${lineHeight}${lineHeightUnit}`;

  const letterSpacing = toNumber(typography.letterSpacing, 0);
  if (letterSpacing !== 0) {
    styles.letterSpacing = `${letterSpacing}${asString(typography.letterSpacingUnit) ?? "px"}`;
  }

  const wordSpacing = toNumber(typography.wordSpacing, 0);
  if (wordSpacing !== 0) {
    styles.wordSpacing = `${wordSpacing}px`;
  }

  return sanitizeStyleMap(styles);
}

function buildSpacingStyles(spacing: Record<string, unknown>): Record<string, string> {
  const padding = isRecord(spacing.padding) ? spacing.padding : null;
  const margin = isRecord(spacing.margin) ? spacing.margin : null;

  const formatSpacing = (value: Record<string, unknown>): string => {
    const unit = asString(value.unit) ?? "px";
    const top = toNumber(value.top, 0);
    const right = toNumber(value.right, 0);
    const bottom = toNumber(value.bottom, 0);
    const left = toNumber(value.left, 0);

    if (top === right && right === bottom && bottom === left) {
      return `${top}${unit}`;
    }

    return `${top}${unit} ${right}${unit} ${bottom}${unit} ${left}${unit}`;
  };

  return sanitizeStyleMap({
    ...(padding ? { padding: formatSpacing(padding) } : {}),
    ...(margin ? { margin: formatSpacing(margin) } : {}),
  });
}

function buildBorderStyles(border: Record<string, unknown>): Record<string, string> {
  const style = asString(border.style) ?? "none";
  if (style === "none") {
    return { border: "none" };
  }

  const widthTop = toNumber(border.widthTop, 0);
  const widthRight = toNumber(border.widthRight, 0);
  const widthBottom = toNumber(border.widthBottom, 0);
  const widthLeft = toNumber(border.widthLeft, 0);
  const color = asString(border.color) ?? "var(--ease-color-border,var(--ease-color-muted,#d1d5db))";
  const radiusTopLeft = toNumber(border.radiusTopLeft, 0);
  const radiusTopRight = toNumber(border.radiusTopRight, 0);
  const radiusBottomRight = toNumber(border.radiusBottomRight, 0);
  const radiusBottomLeft = toNumber(border.radiusBottomLeft, 0);

  const declarations = parseDeclarations(
    [
      widthTop === widthRight && widthRight === widthBottom && widthBottom === widthLeft
        ? `border: ${widthTop}px ${style} ${color}`
        : [
            `border-style: ${style}`,
            `border-color: ${color}`,
            `border-width: ${widthTop}px ${widthRight}px ${widthBottom}px ${widthLeft}px`,
          ].join("; "),
      radiusTopLeft === radiusTopRight
      && radiusTopRight === radiusBottomRight
      && radiusBottomRight === radiusBottomLeft
        ? radiusTopLeft > 0
          ? `border-radius: ${radiusTopLeft}px`
          : ""
        : `border-radius: ${radiusTopLeft}px ${radiusTopRight}px ${radiusBottomRight}px ${radiusBottomLeft}px`,
    ].filter(Boolean).join("; "),
  );

  return sanitizeStyleMap(declarations);
}

function extractStateObject(props: Record<string, unknown>, state: StyleState): Record<string, unknown> | null {
  const dssStyles = isRecord(props._dssStyles) ? props._dssStyles : null;
  if (dssStyles) {
    if (state === "normal") {
      const { states, ...normal } = dssStyles;
      return Object.keys(normal).length > 0 ? normal : null;
    }

    const nestedStates = isRecord(dssStyles.states) ? dssStyles.states : null;
    if (nestedStates && isRecord(nestedStates[state])) {
      return nestedStates[state] as Record<string, unknown>;
    }
  }

  const styles = isRecord(props._styles) ? props._styles : null;
  if (styles && isRecord(styles[state])) {
    return styles[state] as Record<string, unknown>;
  }

  const legacyKey = state === "normal" ? null : `_${state}Style`;
  if (legacyKey && isRecord(props[legacyKey])) {
    return props[legacyKey] as Record<string, unknown>;
  }

  return null;
}

function extractAnimationObject(props: Record<string, unknown>): Record<string, unknown> | null {
  const normalState = extractStateObject(props, "normal");
  return normalState && isRecord(normalState.animation)
    ? normalState.animation as Record<string, unknown>
    : null;
}

function deriveHoverAnimationStyle(props: Record<string, unknown>): Record<string, string> {
  const animation = extractAnimationObject(props);
  if (!animation) return {};

  const transforms: string[] = [];
  const hoverScale = animation.hoverScale;
  if (typeof hoverScale === "number" && hoverScale !== 1) {
    transforms.push(`scale(${hoverScale})`);
  }

  const hoverTranslateY = animation.hoverTranslateY;
  if (typeof hoverTranslateY === "number" && hoverTranslateY !== 0) {
    transforms.push(`translateY(${hoverTranslateY}px)`);
  }

  const style: Record<string, string> = {};
  if (transforms.length > 0) {
    style.transform = transforms.join(" ");
  }

  const hoverShadow = asString(animation.hoverShadow);
  if (hoverShadow && hoverShadow !== "none") {
    style.boxShadow = hoverShadow;
  }

  return sanitizeStyleMap(style);
}

function buildHoverPresetCss(className: string, props: Record<string, unknown>): string {
  const animation = extractAnimationObject(props);
  const presetId = asString(animation?.hoverPreset);
  if (!presetId) return "";

  const preset = findHoverPreset(presetId);
  if (!preset) return "";

  const safeKeyframeName = `ease-hover-${className}-${preset.id}`.replace(/[^a-zA-Z0-9_-]/g, "-");
  return preset.buildCss({ className, keyframeName: safeKeyframeName });
}

function buildAnimationTransition(props: Record<string, unknown>): string {
  const animation = extractAnimationObject(props);
  if (!animation) {
    return "all 200ms ease";
  }

  const durationMs = typeof animation.durationMs === "number"
    ? `${animation.durationMs}ms`
    : "200ms";
  const easing = asString(animation.easing) ?? "ease";
  const property = asString(animation.transition) ?? "all";

  return `${sanitizeCssValue(property)} ${sanitizeCssValue(durationMs)} ${sanitizeCssValue(easing)}`;
}

export function extractBlockStyleMap(
  props: Record<string, unknown>,
  state: StyleState,
): Record<string, string> {
  const styleState = extractStateObject(props, state);
  if (!styleState) return {};

  if (state !== "normal" && !styleState.background && !styleState.typography && !styleState.spacing && !styleState.border && !styleState.text && !styleState.shadow) {
    return sanitizeStyleMap(styleState as Record<string, string>);
  }

  const styleMap: Record<string, string> = {};

  if (isRecord(styleState.background)) {
    Object.assign(styleMap, buildBackgroundStyles(styleState.background));
  }

  if (isRecord(styleState.typography)) {
    Object.assign(styleMap, buildTypographyStyles(styleState.typography));
  }

  if (isRecord(styleState.spacing)) {
    Object.assign(styleMap, buildSpacingStyles(styleState.spacing));
  }

  if (isRecord(styleState.border)) {
    Object.assign(styleMap, buildBorderStyles(styleState.border));
  }

  if (isRecord(styleState.text)) {
    const color = asString(styleState.text.color);
    if (color) styleMap.color = color;
    const textAlign = asString(styleState.text.align);
    if (textAlign) styleMap.textAlign = textAlign;
  }

  const shadow = asString(styleState.shadow);
  if (shadow && shadow !== "none") {
    styleMap.boxShadow = shadow;
  }

  if (state === "hover") {
    Object.assign(styleMap, deriveHoverAnimationStyle(props));
  }

  return sanitizeStyleMap(styleMap);
}

export function buildStateCss(component: PuckComponent): string {
  if (!isRecord(component.props)) return "";
  const componentId = asString(component.props.id);
  if (!componentId) return "";

  const className = buildScopedClassName(componentId);
  if (!VALID_SCOPED_CLASS.test(className)) return "";
  const dssStyles = isRecord(component.props._dssStyles) ? component.props._dssStyles as DssStyles : null;

  const hover = extractBlockStyleMap(component.props, "hover");
  const focus = extractBlockStyleMap(component.props, "focus");
  const active = extractBlockStyleMap(component.props, "active");
  const transition = buildAnimationTransition(component.props);
  const hoverAnimation = deriveHoverAnimationStyle(component.props);
  const hoverPresetCss = buildHoverPresetCss(className, component.props);
  const backgroundEffectCss = buildBackgroundEffectCss(className, dssStyles?.background);
  const nextHover = Object.keys(hover).length > 0
    ? hover
    : hoverAnimation;

  const hasHover = Object.keys(nextHover).length > 0 || hoverPresetCss.length > 0;
  const hasFocus = Object.keys(focus).length > 0;
  const hasActive = Object.keys(active).length > 0;
  const hasInteractiveAnimation = transition !== "all 200ms ease";
  const hasBackgroundEffect = backgroundEffectCss.length > 0;

  if (!hasHover && !hasFocus && !hasActive && !hasInteractiveAnimation && !hasBackgroundEffect) {
    return "";
  }

  const rules = [
    backgroundEffectCss,
    `.${className} { transition: ${transition}; }`,
  ].filter(Boolean);
  if (Object.keys(nextHover).length > 0) {
    rules.push(`.${className}:hover { ${serializeStyleMap(nextHover)} }`);
  }
  if (hoverPresetCss) rules.push(hoverPresetCss);
  if (hasFocus) rules.push(`.${className}:focus { ${serializeStyleMap(focus)} }`);
  if (hasActive) rules.push(`.${className}:active { ${serializeStyleMap(active)} }`);
  return rules.join("\n");
}

export function buildPublishedStateStyleTag(components: PuckComponent[]): string {
  const css = components
    .map((component) => buildStateCss(component))
    .filter(Boolean)
    .join("\n");

  if (!css) return "";
  return `<style data-ease-block-states>\n${css}\n</style>`;
}

export function extractBaseInlineStyles(props: Record<string, unknown>): Record<string, string> {
  return extractBlockStyleMap(props, "normal");
}

export function serializeStyleMap(styleMap: Record<string, string>): string {
  return Object.entries(styleMap)
    .filter(([key, value]) => ALLOWED_STYLE_PROPERTIES.has(key) && value)
    .map(([key, value]) => `${camelToKebab(key)}: ${sanitizeCssValue(value)};`)
    .join(" ");
}
